import * as React           from "react";
import {cx}                 from "emotion";

import "./cc-integration.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Invoke}           from "@Common/react/invoke";
import {Load}             from "@Common/react/load";
import {UseState}         from "@Common/react/use-state";
import {getGalleryParams} from "@Common/tools/cc-integration/get-gallery-params";
import {isDevXd}          from "@Common/ui-components/debug/is-dev";
import {AlertModal}      from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";

import {CcEditor} from "./cc-editor";

export const CcIntegration = ({
    userData,
    idmlPath,
    apis,
    dataProperties,
    itemId = undefined,
    reload,
    next,
}) => cs(
    consumeContext("systemConstants"),
    consumeContext("ccApis"),
    consumeContext("ccHostResponse"),
    ["changed", (_, next) => UseState({next})],
    ["alertModal", ({}, next) => AlertModal({next})],
    ["ccConfig", ({ccApis}, next) => Load({
        _key: userData && JSON.stringify(userData.templateIds),
        fetch: async () => userData && ccApis.getTemplateConf({...userData, itemId}),
        next,
    })],
    ["ccEditor", ({ccApis, ccHostResponse, changed, alertModal, ccConfig, systemConstants}, next) => <CcEditor {...{
        apis,
        ccApis,
        ccHostResponse,
        userData,
        ccConfig,
        idmlPath,
        itemId,
        changedSinceLastSave: changed.value,
        systemConstants: systemConstants.value,
        reload,
        onNotifyFailover: async (internetIsConnected, failoverServerIsAvailable, changesSaved) => {

            // remove the CC host - this will let other components know to suppress functionality due to a known connectivity issue
            ccHostResponse.onChange({...ccHostResponse.value, ccHost: null});

            // build the description based from failover availability and save status
            let description = "There was an error communicating with the server. ";
            if (!internetIsConnected) description += "Please check your connection and reload to continue. ";
            else if (!failoverServerIsAvailable) description += "We have been notified of the issue. Please try again later. ";
            else {
                description += "You must reload to continue. ";
                if (changesSaved) description += "All changes have been saved. ";
                else description += "Any changes since your last save will be lost. ";
            }

            // display the alert and refresh page after user accepts
            await alertModal.show({
                actionName: "reload",
                message: "Network Error",
                description,
            });
            location.reload();

        },
        next,
    }}/>],
    ["allowedGalleryTabs", (_, next) => Load({
        _key: `galleryTabs-${userData?.sourceData?.xd?.company?.idCompany}`,
        fetch: async () => {
            if (!userData?.sourceData?.xd?.company?.idCompany) return null;

            return await apis.next.gallery.getAllowedPhotoGalleryTabs({
                idCompany: userData.sourceData.xd.company.idCompany,
                back: userData.templateIds[userData.templateIds.length-1]
            });

        },
        next,
    })],
    ({allowedGalleryTabs, ccEditor, ccHostResponse, changed, ccConfig}) => {
        return next({
            render: ({heightPerWidthRatio}) => cs(
                ["imageSize", (_, next) => UseState({next})],
                ({imageSize}) => (
                    <div
                        className="cc-integration cc-integration-9cv"
                        xd-qa-type="canvas"
                    >
                        <div
                            className={cx("iframe-wrapper show")}
                        >
                            <iframe ref={ccEditor.iframeRef} data-hj-allow-iframe=''/>
                        </div>
                    </div>
                )
            ),

            initialized: ccEditor.initialized,
            error: ccEditor.error,
            items: ccEditor.items,
            changed: changed.value,

            registerCustomGallery: ({onOpen}) => cs(
                ({}, next) => !ccEditor.initialized ? null : next(),
                ({}, next) => !userData?.sourceData.xd.company.prefs?.customersCanvasUsesDefaultAssetManager ? next() : null,
                () => Invoke({
                    props: async () => {
                        // load gallery params and return if no image area found
                        const galleryParams = await getGalleryParams({
                            ccEditor,
                            userData,
                            userCreatedFieldSettings: dataProperties.value,
                            allowedGalleryTabs,
                            adminTool: true
                        });
                        if (!galleryParams) return;

                        // open Photo Dashboard if an image area was found
                        onOpen({
                            ...galleryParams,
                            templateId: userData?.templateIds?.[userData.templateIds.length-1],
                            orderId: 0,
                            photoServiceEmail: userData.sourceData.xd.user?.agentEmail,
                            listing: userData.sourceData.xd.listing,
                            adminTool: true
                        });
                    },
                    action: ({getLatestProps}) => {
                        const CustomersCanvas = window["CustomersCanvas"] || null;
                        ccEditor.subscribe(
                            // @ts-ignore
                            CustomersCanvas.IframeApi.PostMessage.Events.AssetManagerOpened,
                            async () => {
                                const action = getLatestProps();
                                action();
                            },
                        );
                        // don't allow the editor to display the default asset manager if using the custom gallery
                        ccEditor.hideDefaultAssetManager();
                    },
                }),
            ),

            clearChanged: async () => {
                changed.onChange(null);
            },
            selectField: async (selection) => {
                ccEditor.editor.selectItems(selection);
            },

            saveProductModel: async () => {
                await ccEditor.saveProductModel();
            },

            loadProductModel: async () => {
                await ccEditor.loadProductModel();
                changed.onChange(null);
            },

            save: async (stateId) => {
                try {
                    let saved = await ccEditor.saveProduct(stateId);
                    changed.onChange(null);
                    return saved;
                }
                catch(err) {
                    isDevXd() ? console.log("err", err) : null;
                    return
                }
            },

            saveEditingState: async () => {
                try {
                    let response = await ccEditor.saveEditingState();
                    changed.onChange(null);
                    return response;
                }
                catch(err) {
                    isDevXd() ? console.log("err", err) : null;
                    return
                }
            },

            getProduct: async () => await ccEditor.getProduct(),
            reloadItems: async () => await ccEditor.reloadItems(),

            sides: ccEditor.surfaces && ccEditor.surfaces.map((surface) => surface.name),
            currentSide: ccEditor.surfaces && ccEditor.currentSide != null && ccEditor.surfaces[ccEditor.currentSide].name,
            currentSideIndex: ccEditor.surfaces && ccEditor.currentSide,
            changeSide: (sideIndex) => ccEditor.changeSide(sideIndex, true),
            ccHost: ccHostResponse.value.ccHost,
            ccStateId: ccEditor.ccStateId,
            ccVersion: ccEditor.ccVersion,
            ccConfig,
            editingStateId: ccEditor.editingStateId,
            selectedItemName: ccEditor.selectedItemName
        });
    },
);